import { useCallback, useEffect, useMemo, useState } from 'react';
import { Archive, KeyRound, Palette, Save, Settings, User, X } from 'lucide-react';
import OrgSpiritualConfigPanel from './OrgSpiritualConfigPanel';
import api from '../../services/api';
import { useAuth } from '../../context/useAuth';
import { useI18n } from '../../context/useI18n';
import { emitToast } from '../../context/toast';
import { orgDisplayName } from '../../utils/organizationView';
import { userRoles, makeHasAnyRole } from '../dashboard/roles';
import WhiteLabelFields from './WhiteLabelFields';
import OrgOperatorPanel from './OrgOperatorPanel';
import PhoneField from '../ui/PhoneField';

const TABS = [
  { id: 'general', labelKey: 'orgs.tab.general', fallback: 'General', Icon: User },
  { id: 'marca', labelKey: 'orgs.tab.brand', fallback: 'Marca', Icon: Palette },
  { id: 'admin', labelKey: 'org.operator.title', fallback: 'Administrador', Icon: KeyRound },
  { id: 'config', labelKey: 'orgs.tab.config', fallback: 'Configuración', Icon: Settings },
];

const emptyBranding = {
  logo_url: '',
  brand_name: '',
  brand_slug: '',
  welcome_message: '',
  support_whatsapp: '',
  primary_color: '#2563eb',
  secondary_color: '#10b981',
  white_label_enabled: true,
  favicon_url: '',
  cover_url: '',
  social_links: {},
  custom_domain: '',
};

export default function ManageCoachOrgModal({
  trainer,
  operator,
  open,
  onClose,
  onSaved,
  onArchived,
  onManageLicenses,
}) {
  const { t } = useI18n();
  const { user } = useAuth();
  const isSuperAdmin = useMemo(
    () => makeHasAnyRole(userRoles(user))(['super_admin']),
    [user],
  );
  const visibleTabs = useMemo(
    () => TABS.filter(({ id }) => id !== 'config' || isSuperAdmin),
    [isSuperAdmin],
  );
  const [tab, setTab] = useState('general');
  const [general, setGeneral] = useState({ nombre: '', email: '', telefono: '', descripcion: '', experticia: '', especialidad: '' });
  const [branding, setBranding] = useState(emptyBranding);
  const [loadingBrand, setLoadingBrand] = useState(false);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [error, setError] = useState('');

  const displayName = trainer ? orgDisplayName(trainer) || trainer.nombre : '';

  const loadBranding = useCallback(async () => {
    if (!trainer?.id) return;
    setLoadingBrand(true);
    try {
      const { data } = await api.get(`/trainers/${trainer.id}/branding`);
      const b = data?.branding || data || {};
      setBranding({
        ...emptyBranding,
        logo_url: b.logo_url || '',
        brand_name: b.brand_name || trainer.nombre || '',
        brand_slug: b.brand_slug || '',
        welcome_message: b.welcome_message || '',
        support_whatsapp: b.support_whatsapp || '',
        primary_color: b.primary_color || '#2563eb',
        secondary_color: b.secondary_color || '#10b981',
        white_label_enabled: b.white_label_enabled !== false,
        favicon_url: b.favicon_url || '',
        cover_url: b.cover_url || '',
        social_links: b.social_links || {},
        custom_domain: b.custom_domain || '',
      });
      setGeneral((prev) => ({ ...prev, descripcion: b.welcome_message || '' }));
    } catch {
      setError(t('wl.load_error', 'No se pudo cargar la marca'));
    } finally {
      setLoadingBrand(false);
    }
  }, [trainer?.id, trainer?.nombre, t]);

  useEffect(() => {
    if (!open || !trainer) return;
    setTab('general');
    setError('');
    setGeneral({
      nombre: trainer.nombre || '',
      email: trainer.email || '',
      telefono: trainer.telefono || trainer.teléfono || '',
      descripcion: '',
      experticia: Array.isArray(trainer.certificaciones) ? trainer.certificaciones.join(', ') : (trainer.certificaciones || ''),
      especialidad: trainer.especialidad || '',
    });
    loadBranding();
  }, [open, trainer, loadBranding]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !saving && !archiving) onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, saving, archiving]);

  if (!open || !trainer) return null;

  const saveGeneral = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.put(`/trainers/${trainer.id}`, {
        nombre: general.nombre.trim(),
        email: general.email.trim().toLowerCase(),
        telefono: general.telefono?.trim() || null,
        especialidad: general.especialidad?.trim() || null,
        certificaciones: general.experticia?.trim()
          ? general.experticia.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
        welcome_message: general.descripcion?.trim() || null,
      });
      emitToast({ type: 'success', title: t('orgs.coach_updated', 'Organización actualizada') });
      onSaved?.();
    } catch (err) {
      setError(err.response?.data?.error || t('orgs.coach_update_error', 'No se pudo actualizar'));
    } finally {
      setSaving(false);
    }
  };

  const saveBranding = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.put(`/trainers/${trainer.id}`, branding);
      emitToast({ type: 'success', title: t('wl.saved', 'Marca guardada') });
      onSaved?.();
    } catch (err) {
      setError(err.response?.data?.error || t('wl.save_error', 'Error al guardar marca'));
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    const msg = t('orgs.archive_coach_confirm', '¿Archivar esta organización coach? Desaparecerá de listados y selectores, pero se conservará el histórico.');
    if (!window.confirm(msg)) return;
    setArchiving(true);
    setError('');
    try {
      await api.delete(`/trainers/${trainer.id}`);
      emitToast({ type: 'success', title: t('orgs.coach_archived', 'Organización archivada') });
      onArchived?.();
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || t('orgs.coach_archive_error', 'No se pudo archivar'));
    } finally {
      setArchiving(false);
    }
  };

  return (
    <div className="form-modal-overlay" style={{ zIndex: 9998 }} role="dialog" aria-modal="true" onClick={() => !saving && !archiving && onClose?.()}>
      <div className="form-modal w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="form-modal-header">
          <div className="min-w-0">
            <h3 className="font-bold truncate">{t('orgs.manage_coach', 'Gestionar coach')}: {displayName}</h3>
            <p className="text-xs text-stone-500 mt-0.5">{trainer.email}</p>
          </div>
          <button type="button" className="form-close-btn shrink-0" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>

        <div className="px-4 pt-3 flex flex-wrap gap-2 border-b border-slate-200">
          {visibleTabs.map(({ id, labelKey, fallback, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                tab === id ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t(labelKey, fallback)}
            </button>
          ))}
        </div>

        <div className="form-modal-content">
          {error ? <p className="text-red-600 text-sm mb-4">{error}</p> : null}

          {tab === 'general' && (
            <form onSubmit={saveGeneral} className="space-y-4">
              <label className="block text-sm">
                <span className="font-semibold">{t('orgs.field_coach_name', 'Nombre de la organización')}</span>
                <input className="input mt-1 w-full" required value={general.nombre} onChange={(e) => setGeneral((p) => ({ ...p, nombre: e.target.value }))} />
              </label>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="font-semibold">{t('common.email', 'Email')}</span>
                  <input className="input mt-1 w-full" type="email" required value={general.email} onChange={(e) => setGeneral((p) => ({ ...p, email: e.target.value }))} />
                </label>
                <PhoneField
                  label={t('common.phone', 'Teléfono')}
                  value={general.telefono}
                  onChange={(v) => setGeneral((p) => ({ ...p, telefono: v }))}
                  inputClassName="input mt-1 w-full"
                  className="block text-sm"
                  labelClassName="font-semibold"
                />
              </div>
              <label className="block text-sm">
                <span className="font-semibold">{t('orgs.coach_description', 'Descripción')}</span>
                <textarea
                  className="input mt-1 w-full min-h-[72px]"
                  value={general.descripcion}
                  onChange={(e) => setGeneral((p) => ({ ...p, descripcion: e.target.value }))}
                />
              </label>
              <label className="block text-sm">
                <span className="font-semibold">{t('orgs.coach_expertise', 'Experticia')}</span>
                <textarea
                  className="input mt-1 w-full min-h-[64px]"
                  value={general.experticia}
                  onChange={(e) => setGeneral((p) => ({ ...p, experticia: e.target.value }))}
                  placeholder={t('orgs.coach_expertise_hint', 'Separa varias áreas con comas')}
                />
              </label>
              <label className="block text-sm">
                <span className="font-semibold">{t('orgs.coach_specialty', 'Especialidad')}</span>
                <input
                  className="input mt-1 w-full"
                  value={general.especialidad}
                  onChange={(e) => setGeneral((p) => ({ ...p, especialidad: e.target.value }))}
                />
              </label>
              <div className="form-modal-footer">
                <button type="submit" className="btn-primary inline-flex items-center gap-2" disabled={saving}>
                  <Save size={16} />
                  {saving ? t('common.saving', 'Guardando…') : t('common.save', 'Guardar')}
                </button>
              </div>
            </form>
          )}

          {tab === 'marca' && (
            loadingBrand ? (
              <p className="text-stone-500">{t('common.loading', 'Cargando…')}</p>
            ) : (
              <form onSubmit={saveBranding} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label">{t('gyms.logo_url', 'Logo URL')}</label>
                    <input className="input" value={branding.logo_url} onChange={(e) => setBranding((p) => ({ ...p, logo_url: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label">{t('gyms.brand_name', 'Nombre marca')}</label>
                    <input className="input" value={branding.brand_name} onChange={(e) => setBranding((p) => ({ ...p, brand_name: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label">{t('gyms.primary_color', 'Color primario')}</label>
                    <input type="color" className="input h-10" value={branding.primary_color} onChange={(e) => setBranding((p) => ({ ...p, primary_color: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label">{t('gyms.secondary_color', 'Color secundario')}</label>
                    <input type="color" className="input h-10" value={branding.secondary_color} onChange={(e) => setBranding((p) => ({ ...p, secondary_color: e.target.value }))} />
                  </div>
                </div>
                <label className="block">
                  <span className="label">{t('gyms.welcome_message', 'Mensaje bienvenida')}</span>
                  <textarea className="input min-h-[72px]" value={branding.welcome_message} onChange={(e) => setBranding((p) => ({ ...p, welcome_message: e.target.value }))} />
                </label>
                <WhiteLabelFields formData={branding} setFormData={setBranding} />
                <div className="form-modal-footer">
                  <button type="submit" className="btn-primary inline-flex items-center gap-2" disabled={saving}>
                    <Save size={16} />
                    {saving ? t('common.saving', 'Guardando…') : t('common.save', 'Guardar marca')}
                  </button>
                </div>
              </form>
            )
          )}

          {tab === 'admin' && (
            <OrgOperatorPanel entityType="trainer" entityId={trainer.id} />
          )}

          {tab === 'config' && isSuperAdmin && (
            <OrgSpiritualConfigPanel
              entityType="trainer"
              entityId={trainer.id}
              onSaved={onSaved}
            />
          )}
        </div>

        <div className="px-4 pb-4 flex flex-wrap gap-2 justify-between border-t border-slate-200 pt-4">
          <div className="flex flex-wrap gap-2">
            {operator && onManageLicenses ? (
              <button
                type="button"
                className="btn-secondary text-sm"
                onClick={() => onManageLicenses({ operator, orgName: displayName })}
              >
                {t('orgs.licenses', 'Licencias')}
              </button>
            ) : null}
          </div>
          <button
            type="button"
            className="btn-danger text-sm inline-flex items-center gap-1.5"
            disabled={archiving || saving}
            onClick={handleArchive}
          >
            <Archive className="w-4 h-4" />
            {archiving ? t('common.saving', 'Guardando…') : t('orgs.archive', 'Archivar')}
          </button>
        </div>
      </div>
    </div>
  );
}
